/**
 * Transitions entre deux écrans, par l'API View Transitions du navigateur.
 *
 * Le navigateur capture l'écran de départ, on change d'écran, il capture
 * l'écran d'arrivée et anime de l'un à l'autre. Le mouvement est écrit en CSS,
 * dans `theme/base.css`, sur les pseudo-éléments `::view-transition-*` ;
 * l'attribut `data-transition` posé sur `<html>` dit lequel jouer.
 *
 * La transition est lancée ici et non par l'option `viewTransition` de React
 * Router : l'application monte un `<BrowserRouter>` (mode déclaratif), où
 * cette option est sans effet — elle n'existe qu'en mode données.
 * `flushSync` force React à peindre le nouvel écran DANS le rappel de
 * `startViewTransition`, sans quoi la capture d'arrivée serait encore
 * l'ancien écran.
 *
 * Pas de bibliothèque de plus : le navigateur fait la capture et la
 * composition. Un navigateur sans l'API, ou réglé en mouvement réduit, change
 * d'écran instantanément, comme avant.
 */
import { useCallback } from "react";
import type { MouseEvent } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import type { NavigateOptions } from "react-router-dom";
import { prefersReducedMotion } from "./gsap";

/**
 * - `cube-avant` : l'écran courant tourne vers la gauche comme la face d'un
 *   cube, le suivant arrive par la droite (vitrine → connexion, connexion →
 *   inscription) ;
 * - `cube-arriere` : le même cube dans l'autre sens (retour à la connexion) ;
 * - `plongee` : l'écran de connexion recule et s'efface, l'application vient
 *   vers soi (connexion réussie).
 */
export type TransitionEcran = "cube-avant" | "cube-arriere" | "plongee";

function transitionPossible(): boolean {
  return typeof document.startViewTransition === "function" && !prefersReducedMotion();
}

/**
 * Change d'écran avec un effet. Rend une fonction `naviguer(vers, effet)` à
 * appeler à la place de `navigate()`.
 *
 * `avant` s'exécute dans la transition, juste avant la navigation : c'est là
 * qu'un changement d'état qui changerait l'écran à lui seul (une session
 * posée, que les gardes de route redirigent aussitôt) doit avoir lieu, sinon
 * la capture de départ montrerait déjà l'écran d'arrivée.
 */
export function useNavigationTransition(): (
  vers: string,
  effet: TransitionEcran,
  options?: NavigateOptions & { avant?: () => void },
) => void {
  const navigate = useNavigate();
  return useCallback(
    (vers, effet, options) => {
      const { avant, ...navigation } = options ?? {};
      if (!transitionPossible()) {
        avant?.();
        navigate(vers, navigation);
        return;
      }
      document.documentElement.dataset.transition = effet;
      const transition = document.startViewTransition(() => {
        flushSync(() => {
          avant?.();
          navigate(vers, navigation);
        });
      });
      // L'attribut ne sert que pendant la transition : retiré à la fin, il ne
      // teinte aucune autre transition de vue que le navigateur jouerait.
      void transition.finished.finally(() => {
        delete document.documentElement.dataset.transition;
      });
    },
    [navigate],
  );
}

/**
 * Fabrique les props d'un `Link` ou d'un `ButtonLink` qui change d'écran avec
 * un effet. Un seul appel du hook par composant, puis une prop par lien :
 *
 * ```tsx
 * const lien = useLiensTransition();
 * <Link to={ROUTES.connexion} {...lien(ROUTES.connexion, "cube-avant")}>
 * ```
 *
 * Un clic modifié (nouvel onglet, Ctrl, clic milieu) garde le comportement du
 * lien.
 */
export function useLiensTransition(): (
  vers: string,
  effet: TransitionEcran,
) => { onClick: (e: MouseEvent<HTMLAnchorElement>) => void } {
  const naviguer = useNavigationTransition();
  return useCallback(
    (vers, effet) => ({
      onClick: (e: MouseEvent<HTMLAnchorElement>) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
          return;
        }
        e.preventDefault();
        naviguer(vers, effet);
      },
    }),
    [naviguer],
  );
}
